import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { getAuth } from "firebase/auth";
import api from "../api/api"; // Axios instance
import { initialFormData } from '../data/inputData'
import logger from "../logger/logger";
import { PrimaryButtonStyled, SecondaryButtonStyled, SimpleButtonStyled } from "../styles/Button";
import { FormNavContainerStyled, InputContainerStyled, InputStyled } from "../styles/Form";
import { PageHeader2Styled } from "../styles/Main";
import { StatusMessageAreaStyled, ZipCodeEnterStyled, ZipCodeManagerStyled, ZipCodeSelectionStyled, ZipSelectStyled } from '../styles/Zip'


const ZipForm = ({ user, mode, handleIncrementFormStep, handleDecrementFormStep, zipCodes, setZipCodes, formData, setFormData }) => {
  const [newZip, setNewZip] = useState("");
  const [selectedZips, setSelectedZips] = useState([]);
  const [liveStatus, setLiveStatus] = useState("");  
  
  const liveStatusRef = useRef(null);
  const evtSourceRef = useRef(null);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (evtSourceRef.current) {
        evtSourceRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (liveStatusRef.current) {
      liveStatusRef.current.scrollTop = liveStatusRef.current.scrollHeight;
    }
  }, [liveStatus]);

  const fallbackZip = 44105;

  // --- ZIP Code Manager Handlers ---
  useEffect(() => {
    if (mode === "zip") {
      api.post("/get-zip-codes", {user: user['uid']})
        .then((response) => setZipCodes(response.data))
        .catch((err) => logger.error("Error loading ZIP codes: " + err));
    }
  }, [mode, setZipCodes, user]);

  const handleAddZip = () => {
    if (newZip.trim() === "") return;
    api.post("/add-zip-code", { zip_code: newZip.trim(), user: user['uid'] })
      .then(() => api.post("/get-zip-codes", {user: user['uid']}))
      .then((resp) => {
        setZipCodes(resp.data);
        setNewZip("");
      })
      .catch((err) => logger.error("Error adding ZIP code: " + err));
  };

  const handleRemoveZips = async () => {
    selectedZips.forEach((z) => {
      api.post(`/delete-zip-code`, {zip_code: z, user: user['uid']})
        .then(() => {
          api.post("/get-zip-codes", {user: user['uid']})
            .then((resp) => {
              setZipCodes(resp.data);
              setSelectedZips([]);
            })
            .catch((err) => logger.error("Error refreshing ZIP codes: " + err));
        })
        .catch((err) => logger.error("Error removing ZIP code: " + err));
    });
  };

  // --- Update formData when zipData arrives ---
  const updateFormData = (zipData) => {
    if (mode === "zip" && zipData.length > 0) {
      const newFormData = { ...formData }
      zipData.forEach((item) => {
        newFormData[item.zip_code] = JSON.parse(JSON.stringify(initialFormData));
        newFormData[item.zip_code]["sqft"]["value"] = item.sqft || 0
        newFormData[item.zip_code]["purchase_price"]["value"] = item.purchase_price || 0
        newFormData[item.zip_code]["unit_1_rent"]["value"] = (item.avg_rent_sqft * item.sqft).toString() || 0
        newFormData[item.zip_code]["rent_per_sqft"]["value"] = item.avg_rent_sqft || 0
        newFormData[item.zip_code]["property_tax_rate_pct"]["value"] = item.financial_metrics ? item.financial_metrics.property_tax_rate_percentage : 0
        newFormData[item.zip_code]["homeowners_insurance_cost"]["value"] = item.financial_metrics ? item.financial_metrics.average_homeowners_insurance_cost : 0
        newFormData[item.zip_code]["monthly_utilities_cost"]["value"] = item.financial_metrics ? item.financial_metrics.monthly_average_utility_cost : 0
        newFormData[item.zip_code]["annual_property_tax_increase_pct"]["value"] = item.financial_metrics ? item.financial_metrics.annual_property_tax_increase_percentage : 0
        newFormData[item.zip_code]["cap_rate"]["value"] = item.financial_metrics ? item.financial_metrics.average_cap_rate_b_c_properties : 0
        
      });
      setFormData(newFormData)
      logger.info(`General and ZIP-specific data has been autofilled.`)
      handleIncrementFormStep()      
    }
  };

  // --- Fetch Initial Data via SSE for live status ---
  const startLiveFetch = async () => {
    setLiveStatus("");
    const zipParam = mode === "manual" ? fallbackZip : zipCodes.join(",");
    if (!zipParam) return;
    // Close any existing connection before starting new one
    if (evtSourceRef.current) {
      evtSourceRef.current.close();
    }
    
    // Get Firebase ID token for authentication
    let token = '';
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        token = await auth.currentUser.getIdToken();
      }
    } catch (error) {
      logger.error("Error getting Firebase token:", error);
    }
    
    evtSourceRef.current = new EventSource(
      `${api.defaults.baseURL}/stream-initial-data?zip_codes=${zipParam}&token=${token}`
    );
    evtSourceRef.current.onmessage = (event) => {
      if (event.data.startsWith("final:")) {
        const payloadStr = event.data.replace("final:", "");
        try {
          const payload = JSON.parse(payloadStr);
          updateFormData(payload.zip_data)
          logger.info(`Returned with status: ${payload.status_messages}`)
        } catch (error) {
          logger.error("Error parsing final payload: " + error);
        }
        evtSourceRef.current.close();
        evtSourceRef.current = null;
      } else {
        setLiveStatus((prev) => prev + event.data + "\n");
      }
    };
    evtSourceRef.current.onerror = (err) => {
      logger.error("EventSource failed:", err);
      if (evtSourceRef.current) {
        evtSourceRef.current.close();
        evtSourceRef.current = null;
      }
    };
  };

  return (
    <div>
      {mode === "zip" && (
        <>
          <PageHeader2Styled>Manage ZIP Codes</PageHeader2Styled>
          <ZipCodeManagerStyled>
            <ZipCodeEnterStyled>
              <InputContainerStyled $size={20} $mobileSize={50}>
                <InputStyled
                  type="text"
                  placeholder="Enter ZIP code"
                  value={newZip}
                  onChange={(e) => setNewZip(e.target.value)}
                />
              </InputContainerStyled>
              
              <SimpleButtonStyled onClick={handleAddZip}>Add ZIP Code</SimpleButtonStyled>
            </ZipCodeEnterStyled>
            <ZipCodeSelectionStyled>
              <label>Active ZIP Codes:</label>
              <ZipSelectStyled
                multiple
                value={selectedZips}
                onChange={(e) => {
                  const options = Array.from(e.target.selectedOptions, (option) => option.value);
                  setSelectedZips(options);
                }}
              >
                {zipCodes.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </ZipSelectStyled>
              <SimpleButtonStyled onClick={handleRemoveZips}>Remove Selected</SimpleButtonStyled>
            </ZipCodeSelectionStyled>
          </ZipCodeManagerStyled>
          <FormNavContainerStyled>
            <SecondaryButtonStyled onClick={handleDecrementFormStep}>Back</SecondaryButtonStyled>
            <PrimaryButtonStyled onClick={startLiveFetch}>Search</PrimaryButtonStyled>
          </FormNavContainerStyled>
          {liveStatus && (
            <StatusMessageAreaStyled
              ref={liveStatusRef}
              style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #ccc", padding: "5px", marginTop: "10px" }}
            >
              {liveStatus.split("\n").map((msg, idx) => (
                <div key={idx}>{msg}</div>
              ))}
            </StatusMessageAreaStyled>
          )}
        </>
      )}
    </div>
  );
};

ZipForm.propTypes = {
  mode: PropTypes.string.isRequired,
  handleIncrementFormStep: PropTypes.func.isRequired,
  handleDecrementFormStep: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired, 
  setFormData: PropTypes.func.isRequired, 
};

export default ZipForm;